import type { FilterOptions } from '../data/types'
import type { FilterState, Sort } from '../data/filterState'
import styles from './Toolbar.module.css'

/** One removable pill per active filter value (registration state, racing type, vehicle type,
 *  series, when) - Desktop.dc.html's top-bar chips and the mobile chip scroller share this list. */
export function FilterChips({
  filters,
  selected,
  onChange,
}: {
  filters: FilterOptions
  selected: FilterState
  onChange: (next: FilterState) => void
}) {
  return (
    <>
      {selected.regState.map((value) => {
        const opt = filters.registration.find((o) => o.value === value)
        if (!opt) return null
        return (
          <RemovableChip key={`reg-${value}`} label={opt.label} onRemove={() => onChange({ ...selected, regState: selected.regState.filter((v) => v !== value) })} />
        )
      })}
      {selected.raceType.map((value) => {
        const opt = filters.raceType.find((o) => o.value === value)
        if (!opt) return null
        return (
          <RemovableChip key={`race-${value}`} label={opt.label} onRemove={() => onChange({ ...selected, raceType: selected.raceType.filter((v) => v !== value) })} />
        )
      })}
      {selected.vehicleType.map((value) => {
        const opt = filters.vehicleType.find((o) => o.value === value)
        if (!opt) return null
        return (
          <RemovableChip
            key={`vehicle-${value}`}
            label={opt.label}
            onRemove={() => onChange({ ...selected, vehicleType: selected.vehicleType.filter((v) => v !== value) })}
          />
        )
      })}
      {selected.seriesTrackId != null
        ? (() => {
            const opt = filters.series.find((s) => s.trackId === selected.seriesTrackId)
            return opt ? <RemovableChip key="series" label={opt.name} onRemove={() => onChange({ ...selected, seriesTrackId: null })} /> : null
          })()
        : null}
      {selected.when !== 'any' ? (
        <RemovableChip label={selected.when === 'weekend' ? 'This weekend' : 'Next 30 days'} onRemove={() => onChange({ ...selected, when: 'any' })} />
      ) : null}
    </>
  )
}

function RemovableChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <button type="button" aria-label={`Remove filter ${label}`} className={styles.removableChip} onClick={onRemove}>
      {label}
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true">
        <path d="M6 6l12 12M18 6L6 18" />
      </svg>
    </button>
  )
}

export function FiltersOpenerButton({ count, onClick }: { count: number; onClick: () => void }) {
  return (
    <button type="button" className={styles.filtersOpener} onClick={onClick}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
        <path d="M4 6h16M7 12h10M10 18h4" />
      </svg>
      Filters
      {count > 0 ? <span className={styles.filtersOpenerBadge}>{count}</span> : null}
    </button>
  )
}

export type ViewMode = 'list' | 'flyers'

export function ViewToggle({ view, onChange, isMobile }: { view: ViewMode; onChange: (view: ViewMode) => void; isMobile: boolean }) {
  return (
    <div role="group" aria-label="View" className={isMobile ? styles.mobileViewToggle : styles.viewToggle}>
      <button type="button" aria-pressed={view === 'list'} className={`${styles.viewButton} ${view === 'list' ? styles.viewButtonActive : ''}`} onClick={() => onChange('list')}>
        List
      </button>
      {!isMobile ? (
        <button type="button" aria-pressed={view === 'flyers'} className={`${styles.viewButton} ${view === 'flyers' ? styles.viewButtonActive : ''}`} onClick={() => onChange('flyers')}>
          Flyers
        </button>
      ) : null}
      <button type="button" aria-pressed={false} disabled title="Coming soon" className={`${styles.viewButton} ${styles.viewButtonDisabled}`}>
        Calendar
      </button>
      <button type="button" aria-pressed={false} disabled title="Coming soon" className={`${styles.viewButton} ${styles.viewButtonDisabled}`}>
        Map
      </button>
    </div>
  )
}

// 'Nearest' is hidden alongside SearchBar.tsx's SHOW_LOCATION_SEARCH flag - it has nothing to
// sort by without a location set, and that's no longer possible to set via the UI. Sort stays a
// valid value elsewhere (filterState.ts, eventsListApi.ts) so this is just the one dropdown entry.
const SORT_OPTIONS: Array<{ value: Sort; label: string }> = [
  { value: 'soonest', label: 'Soonest' },
  { value: 'mostEntries', label: 'Most entries' },
]

export function SortSelect({ sort, onChange }: { sort: Sort; onChange: (sort: Sort) => void }) {
  return (
    <label className={styles.sortLabel}>
      Sort
      <select className={styles.sortSelect} value={sort} onChange={(e) => onChange(e.target.value as Sort)}>
        {SORT_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  )
}

/** Desktop results toolbar, Desktop.dc.html: "48 events" + active-filter chips on the left,
 *  Sort + ViewToggle on the right. */
export function DesktopToolbar({
  totalCount,
  filters,
  selected,
  onFilterChange,
  sort,
  onSortChange,
  view,
  onViewChange,
}: {
  totalCount: number
  filters: FilterOptions
  selected: FilterState
  onFilterChange: (next: FilterState) => void
  sort: Sort
  onSortChange: (sort: Sort) => void
  view: ViewMode
  onViewChange: (view: ViewMode) => void
}) {
  return (
    <div className={styles.desktopToolbar}>
      <div className={styles.resultsGroup}>
        <span className={styles.resultsCount}>{totalCount} events</span>
        <FilterChips filters={filters} selected={selected} onChange={onFilterChange} />
      </div>
      <div className={styles.controlsGroup}>
        <SortSelect sort={sort} onChange={onSortChange} />
        <ViewToggle view={view} onChange={onViewChange} isMobile={false} />
      </div>
    </div>
  )
}

/** Mobile horizontal chip scroller, Main.dc.html: Filters opener (with active count badge) +
 *  removable active-filter chips. */
export function MobileChipsRow({
  activeCount,
  onOpenFilters,
  filters,
  selected,
  onFilterChange,
}: {
  activeCount: number
  onOpenFilters: () => void
  filters: FilterOptions
  selected: FilterState
  onFilterChange: (next: FilterState) => void
}) {
  return (
    <div className={styles.mobileScroller}>
      <FiltersOpenerButton count={activeCount} onClick={onOpenFilters} />
      <FilterChips filters={filters} selected={selected} onChange={onFilterChange} />
    </div>
  )
}
